// ============================================================================
// ClientNutritionPanel — the trainer's view of one client's nutrition (Wave 2).
// ----------------------------------------------------------------------------
// Read-only: a client owns + logs their own meals/water (via the portal); the
// trainer observes here whether they're on track. Shows, from
// GET /api/clients/{id}/nutrition:
//   • the client's DAILY TARGETS (auto guidance: kcal/protein/carbs/fat/water)
//     with today's LOGGED intake against each as a labelled bar (matching the
//     client-portal NutritionTargetsCard so both sides read the same way), plus
//     an on-track / over / under summary on calories.
//   • the day's RECENT MEALS (title, meal type + time, per-meal macros).
//   • today's WATER total.
// Live-synced: a client logging a meal/water broadcasts `meal`/`water`, mapped
// (ENTITY_INVALIDATIONS) to the nutrition root, so this panel updates with no
// refresh. Mobile-first, Pulse, EN + 繁中, reduced-motion friendly (CSS bars).
// Nutrition is general guidance, NOT medical advice (disclaimer surfaced).
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge, Banner, Card, CardHeader, EmptyState } from "../../components";
import { LoadingBlock } from "../../app/PageState";
import { useClientNutrition } from "../../hooks/nutrition";
import type { Meal, NutritionTargets } from "../../api";
import { fmtNum } from "../../client/pages/portalLib";
import { formatTime } from "../../lib/date";

type Accent = "coral" | "violet" | "ocean" | "lime" | "amber";

const BAR: Record<Accent, string> = {
  coral: "bg-grad-coral",
  violet: "bg-grad-violet",
  ocean: "bg-grad-ocean",
  lime: "bg-grad-lime",
  amber: "bg-grad-amber",
};

function ProgressRow({
  label,
  logged,
  target,
  unit,
  accent,
}: {
  label: string;
  logged: number;
  target: number | null;
  unit: string;
  accent: Accent;
}) {
  const { t } = useTranslation("nutrition");
  const pct =
    target && target > 0 ? Math.min(100, Math.round((logged / target) * 100)) : 0;
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <span className="font-display text-sm font-bold text-body">{label}</span>
        <span className="text-sm tabular-nums text-body-soft">
          {target != null
            ? t("trainer.intakeVsTarget", {
                logged: fmtNum(logged, 0),
                target: fmtNum(target, 0),
                unit,
              })
            : t("trainer.loggedOnly", { logged: fmtNum(logged, 0), unit })}
        </span>
      </div>
      <div
        className="h-2.5 w-full overflow-hidden rounded-pill bg-surface-sunken"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className={`h-full rounded-pill ${BAR[accent]} transition-[width] duration-500 ease-kinetic`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** The calories on-track badge: under / on-track / over the day's kcal target. */
function CalorieStatus({
  logged,
  target,
}: {
  logged: number;
  target: number;
}) {
  const { t } = useTranslation("nutrition");
  const diff = Math.round(target - logged);
  // Within ~5% of target reads as "on track"; otherwise under / over.
  const band = Math.max(50, target * 0.05);
  if (Math.abs(diff) <= band) {
    return (
      <Badge tone="success" dot>
        {t("trainer.onTrack")}
      </Badge>
    );
  }
  if (diff > 0) {
    return (
      <Badge tone="info" dot>
        {t("trainer.remaining", { kcal: fmtNum(diff, 0) })}
      </Badge>
    );
  }
  return (
    <Badge tone="warning" dot>
      {t("trainer.overBy", { kcal: fmtNum(Math.abs(diff), 0) })}
    </Badge>
  );
}

function MealItem({ meal }: { meal: Meal }) {
  const { t } = useTranslation("nutrition");
  const typeLabel = meal.meal ? t(`meal.${meal.meal}`, meal.meal) : "";
  const time = meal.eaten_at ? formatTime(meal.eaten_at) : "";
  const sub =
    typeLabel && time
      ? t("trainer.mealAt", { meal: typeLabel, time })
      : typeLabel || time;
  const hasMacros =
    meal.protein_g != null || meal.carbs_g != null || meal.fat_g != null;
  return (
    <li className="flex items-center gap-3 rounded-md bg-surface p-3 ring-1 ring-line">
      <span className="h-10 w-1.5 shrink-0 rounded-pill bg-grad-amber" aria-hidden />
      <div className="min-w-0 flex-1">
        <span className="block truncate font-display text-sm font-bold text-body">
          {meal.title || t("trainer.untitledMeal")}
        </span>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-body-soft">
          {sub && <span className="truncate">{sub}</span>}
          {hasMacros && (
            <span className="tabular-nums">
              {t("trainer.macroLine", {
                protein: fmtNum(meal.protein_g ?? 0, 0),
                carbs: fmtNum(meal.carbs_g ?? 0, 0),
                fat: fmtNum(meal.fat_g ?? 0, 0),
              })}
            </span>
          )}
        </div>
      </div>
      {meal.kcal != null && (
        <span className="shrink-0 font-display text-sm font-extrabold tabular-nums text-body">
          {fmtNum(meal.kcal, 0)}{" "}
          <span className="text-xs font-bold text-body-soft">
            {t("macro.kcalUnit")}
          </span>
        </span>
      )}
    </li>
  );
}

function Targets({
  targets,
  loggedKcal,
  loggedProtein,
  loggedCarbs,
  loggedFat,
  loggedWater,
}: {
  targets: NutritionTargets;
  loggedKcal: number;
  loggedProtein: number;
  loggedCarbs: number;
  loggedFat: number;
  loggedWater: number;
}) {
  const { t } = useTranslation("nutrition");
  const sourceLabel =
    targets.source === "coach" ? t("target.coach") : t("target.auto");
  const macrosMissing = targets.kcal == null && targets.protein_g == null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("target.source")}: {sourceLabel}
        </p>
        {targets.kcal != null && (
          <CalorieStatus logged={loggedKcal} target={targets.kcal} />
        )}
      </div>

      {macrosMissing ? (
        <Banner tone="info" title={t("trainer.noTargets")} />
      ) : (
        <div className="space-y-4">
          <ProgressRow
            label={t("macro.kcal")}
            logged={loggedKcal}
            target={targets.kcal}
            unit={t("macro.kcalUnit")}
            accent="coral"
          />
          <ProgressRow
            label={t("macro.protein")}
            logged={loggedProtein}
            target={targets.protein_g}
            unit={t("macro.gUnit")}
            accent="violet"
          />
          <ProgressRow
            label={t("macro.carbs")}
            logged={loggedCarbs}
            target={targets.carbs_g}
            unit={t("macro.gUnit")}
            accent="amber"
          />
          <ProgressRow
            label={t("macro.fat")}
            logged={loggedFat}
            target={targets.fat_g}
            unit={t("macro.gUnit")}
            accent="lime"
          />
        </div>
      )}

      <ProgressRow
        label={t("trainer.water")}
        logged={loggedWater}
        target={targets.water_ml}
        unit={t("water.mlUnit")}
        accent="ocean"
      />

      <p className="rounded-md bg-surface-sunken px-3 py-2.5 text-xs leading-relaxed text-body-muted">
        {t("disclaimer")}
      </p>
    </div>
  );
}

export function ClientNutritionPanel({ clientId }: { clientId: number }) {
  const { t } = useTranslation("nutrition");
  const query = useClientNutrition(clientId);

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader title={t("trainer.title")} subtitle={t("trainer.subtitle")} />
        <LoadingBlock />
      </Card>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Card>
        <CardHeader title={t("trainer.title")} subtitle={t("trainer.subtitle")} />
        <Banner tone="warning" title={t("trainer.loadError")} />
      </Card>
    );
  }

  const { targets, meals, totals, water } = query.data;
  const loggedKcal = totals?.kcal ?? 0;
  const loggedProtein = totals?.protein_g ?? 0;
  const loggedCarbs = totals?.carbs_g ?? 0;
  const loggedFat = totals?.fat_g ?? 0;

  // Today's water total (latest row's day; the panel views today's intake).
  const loggedWater = water.length > 0 ? (water[water.length - 1]?.ml ?? 0) : 0;

  return (
    <Card className="space-y-6">
      <CardHeader title={t("trainer.title")} subtitle={t("trainer.subtitle")} />

      {/* Targets vs today's intake */}
      {targets ? (
        <Targets
          targets={targets}
          loggedKcal={loggedKcal}
          loggedProtein={loggedProtein}
          loggedCarbs={loggedCarbs}
          loggedFat={loggedFat}
          loggedWater={loggedWater}
        />
      ) : (
        <Banner tone="info" title={t("trainer.noTargets")} />
      )}

      {/* Recent meals */}
      <div>
        <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("trainer.recentMeals")}
        </p>
        {meals.length === 0 ? (
          <EmptyState title={t("trainer.noMeals")} className="!py-10" />
        ) : (
          <ul className="space-y-2">
            {meals.slice(0, 12).map((m) => (
              <MealItem key={m.id} meal={m} />
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
